import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import axiosInstance from '../../_helpers/axios_instance';

export interface ProviderTitle {
  id: number;
  name: string;
  enabled: boolean;
}

type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface ProviderTitlesState {
  providerTitles: ProviderTitle[];
  status: AsyncStatus;
  error: string | string[] | null;
}

const initialState: ProviderTitlesState = {
  providerTitles: [],
  status: 'idle',
  error: null,
};

export const fetchProviderTitles = createAsyncThunk<
  { data: ProviderTitle[] },
  void,
  { state: RootState }
>(
  'providerTitles/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/api/provider_titles');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.errors || error.message);
    }
  },
  {
    condition: (_, { getState }) => getState().provider_titles.status !== 'loading',
  }
);

const providerTitlesSlice = createSlice({
  name: 'providerTitles',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchProviderTitles.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchProviderTitles.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.providerTitles = action.payload.data
          .filter((a) => a.name)
          .sort((a, b) => a.name.localeCompare(b.name));
      })
      .addCase(fetchProviderTitles.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string | string[]) ?? null;
      });
  },
});

export default providerTitlesSlice.reducer;

export const getEnabledProviderTitleNames = (providerTitles: ProviderTitlesState): string[] =>
  providerTitles.providerTitles.filter((pt) => pt.enabled).map((pt) => pt.name);

